import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'
import GetFocalRatio from '../../helpers/getFocalRatio'
import isDarkColor from '../../helpers/isDarkColor'
import renderDocumentToReactComponents from '../../modules/contentful/renderDocumentToReactComponents/renderDocumentToReactComponents'
import ArrowLink from '../atoms/arrowLink'
import theme from '../atoms/theme'

type CardProps = {
  card: Queries.ContentfulCard
  doubleColCard?: boolean
}

const { colours, breakpoints } = theme

export const StyledGatsbyImage = styled(GatsbyImage)`
  margin: ${(props): string => (props.widecard === 'true' ? '8rem 2.4rem 4.8rem' : '0')};
  max-width: ${(props): string => (props.widecard === 'true' ? '50%' : '')};
  @media only screen and ${breakpoints.toNormalScreen} {
    margin: ${(props): string => (props.widecard === 'true' ? '0 2.4rem 4.8rem' : '0')};
  }
`

export const Content = styled.div`
  padding: 4rem 2.4rem 3rem;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  h3 {
    font-size: 2.4rem;
    line-height: 2.4rem;
    margin-bottom: 0;
  }
  a {
    margin-top: auto;
    width: fit-content;
    color: ${(props): string => (props.darkStyle ? colours.light : '')};
    &:hover::after {
      border-color: ${(props): string => (props.darkStyle ? colours.light : '')};
    }
    // Need this one too or it turns blue when transitioning off of white
    &::after {
      border-color: ${(props): string => (props.darkStyle ? colours.light : '')};
    }
  }
  ul {
    padding-left: 1.6rem;
    margin: 0;
  }
  li {
    list-style-type: '✓';
    padding-left: 1.2rem;
    p {
      margin-top: 0;
      line-height: 2.4rem;
      margin-bottom: 1.6rem;
    }
  }
  li::marker {
    font-size: 1.2em;
  }

  p:empty {
    display: none;
  }

  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 1.8rem 1.6rem 3.2rem;
    font-size: 1.6rem;
    h3 {
      font-size: 2rem;
    }
  }
`

export const StyledCard = styled.div`
  background-color: ${(props): string => (props.backgroundColor ? props.backgroundColor : colours.light)};
  color: ${(props): string => (props.darkStyle ? colours.light : colours.dark)};
  display: flex;
  padding: 0;
  flex-basis: 75%;
  flex-direction: ${(props): string => (props.widecard ? 'row-reverse' : 'column')};
  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: ${(props): string => (props.widecard ? 'column-reverse' : 'column')};
  }
  @media only screen and ${breakpoints.fromNormalScreen} {
    ${Content} {
      max-width: ${(props): string => (props.widecard ? '50%' : '')};
    }
  }
`

const Card = (props: CardProps): JSX.Element => {
  const { image, title, description, link, linkUrl, backgroundColor } =
    props.card
  const imageData = image?.image && getImage(image.image?.gatsbyImageData)
  const focalRatio = GetFocalRatio(image)
  const computedObjectPosition = focalRatio
    ? `${focalRatio.x}% ${focalRatio.y}%`
    : 'center center'
  const wideCard = props.doubleColCard
  const isDark = isDarkColor(backgroundColor)

  return (
    <StyledCard
      widecard={wideCard}
      backgroundColor={backgroundColor}
      darkStyle={isDark}
    >
      {imageData && (
        <StyledGatsbyImage
          image={imageData}
          alt={image.title || 'background image'}
          imgStyle={{
            objectFit: 'cover',
            objectPosition: computedObjectPosition,
          }}
          widecard={{ wideCard }}
        />
      )}
      <Content darkStyle={isDark}>
        <h3>{title}</h3>
        {description?.raw &&
          renderDocumentToReactComponents(
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            description as { raw: string; references: any[] },
          )}
        {linkUrl && (
          <ArrowLink to={linkUrl}>
            <span>{link}</span>
          </ArrowLink>
        )}
      </Content>
    </StyledCard>
  )
}

export default Card

// Card
export const ContentfulCardFragment = graphql`
  fragment ContentfulCardFragment on ContentfulCard {
    id
    __typename
    contentful_id
    link
    linkUrl
    title
    description {
      raw
    }
    backgroundColor
    image {
      title
      id
      focalPoint {
        focalPoint {
          x
          y
        }
      }
      image {
        file {
          details {
            image {
              height
              width
            }
          }
        }
        url
        gatsbyImageData(layout: CONSTRAINED, width: 1680, height: 1050, quality: 80)
      }
    }
  }
`
